import {
  fetchAllFactoringExtensions,
  useDeleteFactoringExtension,
  useGetAllFactoringExtensions,
} from 'lib/api/extension/factoring';
import type { FactoringExtensionDetail, FactoringExtensionListItem } from 'lib/api/extension/factoring/types';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import PageHeader, { type ActionButton } from 'lib/designSystem/PageHeader';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { showAlert, validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

import { AuthState } from '../../../../../services/redux/authSlice';
import ExtensionDetails from './components/ExtensionDetails';
import NewExtensions from './components/NewExtensions';
import {
  buildExtensionsQuery,
  type ExtensionsFilters,
  getExtensionsFiltersConfig,
  getExtensionsHeaders,
  INITIAL_FILTERS,
  PAGE_SIZE,
} from './utils';

const Extensions: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const [filters, setFilters] = useState<ExtensionsFilters>(INITIAL_FILTERS);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [activeQuery, setActiveQuery] = useState('');

  const [selectedExtension, setSelectedExtension] =
    useState<FactoringExtensionListItem | null>(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [openMultiExtension, setOpenMultiExtension] = useState(false);

  const { data, isFetching, refetch } = useGetAllFactoringExtensions(
    dispatch,
    activeQuery,
  );
  const deleteExtensionMutation = useDeleteFactoringExtension(dispatch);

  const extensions = data?.data ?? [];
  const canDeleteExtension = validatePermissions(
    user,
    'financing_extension_delete',
  );

  useEffect(() => {
    if (data?.pagination) {
      setPagination({
        page: data.pagination.page,
        pageSize: data.pagination.pageSize,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages,
      });
    }
  }, [data?.pagination]);

  const triggerSearch = async () => {
    const query = buildExtensionsQuery(filters, {
      page: 0,
      pageSize: PAGE_SIZE,
    });
    if (query === activeQuery) {
      await refetch();
    } else {
      setActiveQuery(query);
    }
  };

  const fetchExtensions = async ({
    page = 0,
    pageSize = PAGE_SIZE,
    exportComplete = false,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    if (exportComplete) {
      const query = buildExtensionsQuery(filters, {
        page,
        pageSize,
        all: true,
      });
      return await fetchAllFactoringExtensions(dispatch, query);
    }
    const query = buildExtensionsQuery(filters, { page, pageSize });
    setActiveQuery(query);
    return data;
  };

  const handleShowNetwork = (
    id: string | number,
    idKey: 'businessIdentifier' | 'stakeholderIdentifier',
    nameKey: 'businessName' | 'stakeholderName',
  ) => {
    const row = extensions.find((x) => x.id === Number(id));
    if (!row) return;
    dispatch(showBusinessNetwork({ id: row[idKey], name: row[nameKey] }));
  };

  const handleShowBusinessDetails = (id: string) =>
    handleShowNetwork(id, 'businessIdentifier', 'businessName');

  const handleShowStakeholderDetails = (id: string) =>
    handleShowNetwork(id, 'stakeholderIdentifier', 'stakeholderName');

  const handleExtensionCreated = (extension: FactoringExtensionDetail) => {
    setSelectedExtension(extension as unknown as FactoringExtensionListItem);
    setOpenDetailDialog(true);
  };

  const handleOpenDetail = (id: string | number) => {
    const ext = extensions.find((e) => e.id === Number(id));
    if (!ext) return;
    setSelectedExtension(ext);
    setOpenDetailDialog(true);
  };

  const handleDeleteExtension = async (
    extension: { id?: number | string } & Record<string, unknown>,
  ) => {
    if (!extension.id) return;
    try {
      await deleteExtensionMutation.mutateAsync(extension.id);
      showAlert(dispatch, 'success', 'Prórroga eliminada correctamente');
      setOpenDetailDialog(false);
      setSelectedExtension(null);
      await refetch();
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al eliminar la prórroga');
      throw error;
    }
  };

  const headers = getExtensionsHeaders({
    userIsExpress: !!user?.express,
    onBusinessClick: handleShowBusinessDetails,
    onStakeholderClick: handleShowStakeholderDetails,
  });

  const filtersConfig = getExtensionsFiltersConfig(filters, setFilters);

  const headerActionButtons: ActionButton[] = [
    {
      label: 'Nueva',
      onClick: () => setOpenMultiExtension(true),
      color: 'primary',
      disabled: !validatePermissions(user, 'financing_extension_new'),
    },
  ];

  const globalSearcherProps = {
    filters: filtersConfig,
    triggerSearch,
    dispatch,
    disableSearch: isFetching,
  };

  if (isFetching && extensions.length === 0) {
    return <CenteredCircularProgress type="layout" />;
  }

  return (
    <div>
      <PageHeader.WithTitleAndActions
        title="Prórrogas"
        subtitle="Acá encontrarás todas las prórrogas."
        variant="pageHeader"
        iconType="search"
        actionButtons={headerActionButtons}
        globalSearcherProps={globalSearcherProps}
      />
      <TableWithPagination
        data={extensions}
        setData={() => undefined}
        dataHeaders={headers}
        pagination={pagination}
        setPagination={setPagination}
        searchData={fetchExtensions}
        loading={isFetching}
        setLoading={() => undefined}
        exportToFile="xlsx"
        exportComplete
        handleClickRow={handleOpenDetail}
        lastElement
        finalSumRow
      />
      {openDetailDialog && selectedExtension && (
        <ExtensionDetails
          extension={selectedExtension as unknown as Record<string, unknown>}
          setExtension={(ext) =>
            setSelectedExtension(ext as unknown as FactoringExtensionListItem)
          }
          open={openDetailDialog}
          setOpen={setOpenDetailDialog}
          setUp={triggerSearch}
          edit
          onDeleteExtension={
            canDeleteExtension ? handleDeleteExtension : undefined
          }
        />
      )}
      {openMultiExtension && (
        <NewExtensions
          open={openMultiExtension}
          setOpen={setOpenMultiExtension}
          onCreated={handleExtensionCreated}
        />
      )}
    </div>
  );
};

export default Extensions;
